import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTE } from '@/config';
import type { ServiceResolutionSource } from '@/types';

/**
 * SVC-STEP increment 2 — "Boxy Save · your Karbala rule  [Change]".
 *
 * THE RULE THIS COMPONENT EXISTS FOR: a step that skips itself silently is a
 * decision the merchant cannot audit. The wizard may skip Select Service when
 * something already decided — but the summary must then say WHAT decided, and
 * offer one click back.
 *
 * ⚠️ THE REASON IS KEYED ON THE WIRE'S `source`, NEVER INFERRED. A committed
 * tier plan outranks both the merchant's province rule and their account
 * default (R54-F2), so any client-side guess of the form "they have a default,
 * so say saved default" prints a false claim to a merchant on a plan. That is
 * the same class of defect as naming a destination-blind service in the step's
 * hint (QA SVC-F5), and it is why five variants exist rather than two.
 *
 * The ONE legitimate client-side branch is `order`: an explicit
 * `service_code` on the payload is a FACT the client owns — the merchant
 * clicked it in this session — and it outranks the wire's answer because the
 * backend's ladder puts a per-order choice first too. Everything else comes
 * off the wire.
 */
interface ServiceTraceLineProps {
  /** The service the order will ship on, already localized. */
  serviceTitle: string;
  /** Which rung of the backend ladder decided. */
  source: ServiceResolutionSource;
  /**
   * True when the merchant picked a service on the step in THIS session.
   * Not read from the resolution: the wire cannot know about a click that has
   * not been submitted yet.
   */
  chosenOnThisOrder: boolean;
  /**
   * The destination's localized province name — NOT the rule's stored code.
   * Rules are typed free-form ('KA', 'KRB') and matched on a normalized form
   * (A-F8), so the stored string is often a legacy token that means nothing to
   * a merchant. The destination they picked in Details is by definition the
   * province that matched, and it is already localized in both directions.
   */
  provinceName?: string | null;
  /**
   * Is there more than one live service to change TO?
   *
   * Defaults to true, which is this line's pre-existing behaviour — the only
   * production caller (the summary) passes the catalogue's real answer, and
   * the default keeps a caller that has not been taught rendering as before
   * rather than silently losing the affordance.
   */
  canChange?: boolean;
}

const REASON_KEY: Record<ServiceResolutionSource, string | null> = {
  order: 'service_trace_order',
  plan_assignment: 'service_trace_plan',
  province_rule: 'service_trace_province',
  account_default: 'service_trace_account',
  platform_default: 'service_trace_platform',
  // BOXY-113 — Select because the chosen service cannot reach this address.
  remote_area_fallback: 'service_trace_remote_area',
  // BOXY-530 — Select because the chosen service is not collected from the pickup province.
  pickup_fallback: 'service_trace_pickup',
  // BOXY-547 — Select because the chosen service cannot take a multi-box order.
  multi_box_fallback: 'service_trace_multi_box',
  // Nothing resolved, so there is nothing to name. Rendering "Boxy's default"
  // here would invent a decision that was never made.
  none: null,
};

const ServiceTraceLine = ({
  serviceTitle,
  source,
  chosenOnThisOrder,
  provinceName,
  canChange = true,
}: ServiceTraceLineProps) => {
  const { t } = useTranslation();

  const effectiveSource: ServiceResolutionSource = chosenOnThisOrder ? 'order' : source;
  const reasonKey = REASON_KEY[effectiveSource];

  if (!serviceTitle || reasonKey === null) return null;

  // A province rule with no province name to show would render "your  rule".
  //
  // ⚠️ QA INC2-F1 — the fallback must NOT borrow another rung's wording. It
  // used to say "your saved default", which is false for exactly the merchant
  // it fires on: someone whose account default is Boxy Select and whose
  // Karbala rule sent this order on Boxy Save would be told their saved
  // default decided, naming a service they did not get. A generic phrase is
  // vaguer and TRUE; a specific phrase from a rung that did not apply is the
  // whole class of defect this line exists to prevent.
  const reason =
    effectiveSource === 'province_rule' && !provinceName
      ? String(t('service_trace_province_generic'))
      : String(t(reasonKey, { province: provinceName ?? '' }));

  return (
    <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm'>
      <span className='text-sd-foreground/75'>{String(t('service'))}</span>
      {/* <bdi> for the same reason the step's metrics carry it: a service name
          is a Latin brand inside an Arabic sentence, and an un-isolated mixed
          run is what rendered "16-20" as "20-16" (QA UI-F4). */}
      <bdi className='font-semibold text-sd-foreground'>{serviceTitle}</bdi>
      <span className='text-sd-foreground/75'>·</span>
      <span className='text-sd-foreground/75'>{reason}</span>
      {/* A one-service catalogue has nothing to change TO, and SelectService
          bounces straight back to this page on `services.length < 2`. Offering
          the link anyway is a control that undoes itself — and that is the
          DEFAULT state of an unseeded box, not an edge case. The trace itself
          still renders: naming what decided is useful even when the merchant
          cannot alter it. */}
      {canChange ? (
        <Link
          to={ROUTE.SELECT_SERVICE}
          className='font-semibold text-sd-primary underline underline-offset-2 hover:opacity-80'
        >
          {String(t('change'))}
        </Link>
      ) : null}
    </div>
  );
};

export default ServiceTraceLine;
